import { useState } from "react";
import { Instagram, Mail, MapPin, MessageCircle, Phone, Loader2, RotateCcw, Handshake } from "lucide-react";
import { Link } from "react-router-dom";
import axios from "axios";
import { useLanguage } from "@/context/LanguageContext";

const LOGO_URL =
  "https://customer-assets.emergentagent.com/job_0455e07c-95ba-4da1-86cd-b6b640dcd3e8/artifacts/c1n4bm9e_Haceinda%20Carey%20Cover.png";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Footer() {
  const { t, lang } = useLanguage();
  const [email, setEmail] = useState("");
  const [state, setState] = useState({ status: "idle", message: "" });

  const handleSubmit = async (e) => {
    e.preventDefault();
    const trimmed = email.trim();
    const valid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed);
    if (!valid) {
      setState({ status: "error", message: t.newsletter.error });
      return;
    }
    setState({ status: "loading", message: "" });
    try {
      await axios.post(`${API}/newsletter`, { email: trimmed, locale: lang || "en" });
      setState({ status: "success", message: t.newsletter.success });
      setEmail("");
    } catch (err) {
      setState({ status: "error", message: t.newsletter.error });
    }
  };

  return (
    <footer
      id="contact"
      className="relative bg-[#0A0A0A] text-white"
      data-testid="site-footer"
    >
      {/* Newsletter band */}
      <div className="border-b border-white/10">
        <div className="mx-auto grid max-w-7xl gap-10 px-6 py-16 md:grid-cols-12 md:px-10 md:py-20">
          <div className="md:col-span-6">
            <p className="mb-3 text-[11px] font-semibold uppercase tracking-[0.32em] text-[#FFB800]">
              {t.newsletter.eyebrow}
            </p>
            <h3
              className="text-3xl leading-tight tracking-tight md:text-5xl"
              style={{ fontFamily: "Fraunces, serif" }}
              data-testid="newsletter-title"
            >
              {t.newsletter.title}
            </h3>
            <p className="mt-4 max-w-md text-white/65">{t.newsletter.sub}</p>
          </div>
          <form
            onSubmit={handleSubmit}
            noValidate
            className="flex w-full flex-col gap-3 md:col-span-6 md:self-end"
            data-testid="newsletter-form"
          >
            <div className="flex w-full overflow-hidden rounded-full border border-white/15 bg-white/5 backdrop-blur-md focus-within:border-[#FF5533]">
              <input
                type="text"
                inputMode="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t.newsletter.placeholder}
                data-testid="newsletter-email-input"
                className="w-full bg-transparent px-6 py-4 text-sm text-white placeholder:text-white/40 focus:outline-none"
                aria-label="Email"
              />
              <button
                type="submit"
                disabled={state.status === "loading"}
                data-testid="newsletter-submit-button"
                className="inline-flex shrink-0 items-center gap-2 bg-[#FF5533] px-6 text-sm font-semibold uppercase tracking-wider text-white transition-colors hover:bg-[#E64A2B] disabled:opacity-70"
              >
                {state.status === "loading" ? (
                  <Loader2 size={16} className="animate-spin" />
                ) : null}
                {t.newsletter.button}
              </button>
            </div>
            {state.status === "success" && (
              <p
                className="text-xs text-[#FFB800]"
                data-testid="newsletter-success"
              >
                {state.message}
              </p>
            )}
            {state.status === "error" && (
              <p
                className="text-xs text-[#FF5533]"
                data-testid="newsletter-error"
              >
                {state.message}
              </p>
            )}
          </form>
        </div>
      </div>

      {/* Main footer */}
      <div className="mx-auto grid max-w-7xl gap-12 px-6 py-16 md:grid-cols-12 md:px-10">
        <div className="md:col-span-5">
          <div className="flex items-center gap-3">
            <img
              src={LOGO_URL}
              alt="Hacienda Carey"
              className="h-12 w-12 rounded-full object-cover ring-1 ring-white/10"
            />
            <div>
              <div
                className="text-xl"
                style={{ fontFamily: "Fraunces, serif" }}
              >
                Hacienda Carey
              </div>
              <div className="text-[10px] uppercase tracking-[0.24em] text-[#FFB800]">
                Puerto Rico
              </div>
            </div>
          </div>
          <p className="mt-6 max-w-sm text-sm leading-relaxed text-white/65">
            {t.footer.tagline}
          </p>
          <div className="mt-6 flex items-center gap-2 text-sm text-white/65">
            <MapPin size={14} /> {t.footer.based}
          </div>
        </div>

        <div className="md:col-span-2">
          <h4 className="mb-4 text-[11px] font-semibold uppercase tracking-[0.24em] text-white/45">
            {t.footer.explore}
          </h4>
          <ul className="space-y-2.5 text-sm text-white/80">
            <li>
              <a
                href="#activities"
                className="hover:text-[#FF5533]"
                data-testid="footer-link-activities"
              >
                {t.nav.activities}
              </a>
            </li>
            <li>
              <a
                href="#about"
                className="hover:text-[#FF5533]"
                data-testid="footer-link-about"
              >
                {t.nav.about}
              </a>
            </li>
            <li>
              <a
                href="#reviews"
                className="hover:text-[#FF5533]"
                data-testid="footer-link-reviews"
              >
                {t.nav.reviews}
              </a>
            </li>
          </ul>
        </div>

        <div className="md:col-span-3">
          <h4 className="mb-4 text-[11px] font-semibold uppercase tracking-[0.24em] text-white/45">
            {t.footer.contact}
          </h4>
          <ul className="space-y-2.5 text-sm text-white/80">
            <li>
              <a
                href="mailto:bookings@haciendacarey.com"
                className="inline-flex items-center gap-2 hover:text-[#FF5533]"
                data-testid="footer-email"
              >
                <Mail size={14} /> bookings@haciendacarey.com
              </a>
            </li>
            <li>
              <a
                href="tel:+17878276237"
                className="inline-flex items-center gap-2 hover:text-[#FF5533]"
                data-testid="footer-phone"
              >
                <Phone size={14} /> +1 (787) 827-6237
              </a>
            </li>
            <li>
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 hover:text-[#FF5533]"
                data-testid="footer-contact-form"
              >
                <MessageCircle size={14} /> {lang === "es" ? "Enviar un mensaje" : "Send a message"}
              </Link>
            </li>
            <li>
              <Link
                to="/partner-with-us"
                className="inline-flex items-center gap-2 hover:text-[#FF5533]"
                data-testid="footer-partner-with-us"
              >
                <Handshake size={14} /> {lang === "es" ? "Alianzas" : "Partner With Us"}
              </Link>
            </li>
            <li>
              <Link
                to="/refunds"
                className="inline-flex items-center gap-2 font-semibold text-[#FFB800] hover:text-[#FF5533]"
                data-testid="footer-refund-request"
              >
                <RotateCcw size={14} /> {lang === "es" ? "Solicitud de Reembolso" : "Refund Request"}
              </Link>
            </li>
            <li>
              <a
                href="https://haciendacarey.com"
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-[#FF5533]"
                data-testid="footer-site"
              >
                haciendacarey.com
              </a>
            </li>
          </ul>
        </div>

        <div className="md:col-span-2">
          <h4 className="mb-4 text-[11px] font-semibold uppercase tracking-[0.24em] text-white/45">
            {t.footer.follow}
          </h4>
          <div className="flex items-center gap-2">
            <a
              href="https://www.instagram.com/haciendacareypr"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Instagram @haciendacareypr"
              data-testid="footer-instagram"
              className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-white/15 text-white/85 transition-all hover:-translate-y-0.5 hover:border-[#FF5533] hover:text-[#FF5533]"
            >
              <Instagram size={16} />
            </a>
          </div>
          <p className="mt-3 text-xs text-white/50">@haciendacareypr</p>
        </div>
      </div>

      {/* Bottom bar */}
      <div className="border-t border-white/10">
        <div className="mx-auto flex max-w-7xl flex-col items-start justify-between gap-3 px-6 py-5 text-xs text-white/45 md:flex-row md:items-center md:px-10">
          <span>© {new Date().getFullYear()} Hacienda Carey. {t.footer.rights}</span>
          <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-xs">
            <Link
              to="/privacy"
              className="text-white/65 hover:text-[#FF5533]"
              data-testid="footer-privacy"
            >
              {lang === "es" ? "Privacidad" : "Privacy"}
            </Link>
            <Link
              to="/terms"
              className="text-white/65 hover:text-[#FF5533]"
              data-testid="footer-terms"
            >
              {lang === "es" ? "Términos" : "Terms"}
            </Link>
            <Link
              to="/contact"
              className="text-white/65 hover:text-[#FF5533]"
              data-testid="footer-contact-link"
            >
              {t.nav.contact}
            </Link>
            <Link
              to="/refunds"
              className="text-white/65 hover:text-[#FF5533]"
              data-testid="footer-refunds-link"
            >
              {lang === "es" ? "Solicitud de Reembolso" : "Refund Request"}
            </Link>
            <span className="uppercase tracking-[0.24em] text-white/40">
              Made with care in Puerto Rico
            </span>
          </div>
        </div>
      </div>
    </footer>
  );
}
